import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Spinner } from '../Spinner';
import { FaIcon } from '../FaIcon';
import { Switch } from '../buttons/Swtich';
import { YesNoModal } from '../elements/YesNoModal';
import { getColumnHeaderInformation } from '../dashboard/columns';
import { ClientDashboardColumnsSetsApi } from '../../api/clientDashboardColumnsSets';
import { isPortalModalityToggleEnabled } from '../../utils/FeatureFlags';
import { AssignColumnSetToPortalModal } from './AssignColumnSetToPortalModal';

const CHIPS_VISIBLE_LIMIT = 5;
const MODE_CARDS_V1 = 'CARDS_V1';
const MODE_TABLE_V2 = 'TABLE_V2';

function AssignmentRow({ assignment, isDraggable, isFirst, t, onUnassign, columnHeaderInfo }) {
    const sortable = useSortable({ id: assignment.id, disabled: !isDraggable });
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

    const style = isDraggable
        ? {
            transform: CSS.Transform.toString(transform),
            transition: isDragging ? 'none' : transition,
            opacity: isDragging ? 0.6 : 1,
        }
        : {};

    const columnLabels = useMemo(() => {
        return (assignment.active_columns || []).map((id) => {
            const info = columnHeaderInfo[id];
            return (info && info.name) || prettify(id);
        });
    }, [assignment.active_columns, columnHeaderInfo]);

    const visibleColumns = columnLabels.slice(0, CHIPS_VISIBLE_LIMIT);
    const overflow = columnLabels.slice(CHIPS_VISIBLE_LIMIT);
    const overflowList = overflow.map((c) => `• ${c}`).join('<br/>');

    return (
        <div ref={setNodeRef} style={style} className="client-dashboard-tpl-row">
            {isDraggable && (
                <span
                    className="client-dashboard-tpl-row-handle"
                    {...attributes}
                    {...listeners}
                    title={t('client_portal_dashboard.drag_handle')}
                >
                    <FaIcon iconName="grip-vertical" />
                </span>
            )}

            <span className="client-dashboard-tpl-row-order">
                #{(assignment.order ?? 0) + 1}
            </span>

            <div className="client-dashboard-tpl-row-main">
                <div className="client-dashboard-tpl-row-name">
                    {assignment.name}
                    {isFirst && (
                        <span className="info-icon-container client-dashboard-tpl-row-default-badge">
                            {t('client_portal_dashboard.default_badge')}
                            <div className="warning-tooltip -below -rich">
                                <div className="warning-tooltip-body">
                                    <p className="warning-tooltip-text">
                                        {t('client_portal_dashboard.default_badge_tooltip')}
                                    </p>
                                </div>
                            </div>
                        </span>
                    )}
                    <span className="client-dashboard-tpl-row-count">
                        {' '}· {columnLabels.length} {t('client_portal_dashboard.columns_count_short')}
                    </span>
                </div>
                <div className="client-dashboard-tpl-row-chips">
                    {visibleColumns.map((label, i) => (
                        <span key={`${label}-${i}`} className="client-dashboard-tpl-row-chip">
                            {label}
                        </span>
                    ))}
                    {overflow.length > 0 && (
                        <span className="info-icon-container client-dashboard-tpl-row-overflow">
                            +{overflow.length}
                            <div className="warning-tooltip -below -rich">
                                <div className="warning-tooltip-body">
                                    <p className="warning-tooltip-text"
                                       dangerouslySetInnerHTML={{ __html: overflowList }} />
                                </div>
                            </div>
                        </span>
                    )}
                </div>
            </div>

            <div className="client-dashboard-tpl-row-actions">
                <button
                    type="button"
                    className="client-dashboard-tpl-row-unassign-btn"
                    onClick={() => onUnassign(assignment)}
                    title={t('client_portal_dashboard.action_unassign')}
                >
                    <FaIcon iconName="xmark" />
                    {' '}{t('client_portal_dashboard.action_unassign')}
                </button>
            </div>
        </div>
    );
}

/**
 * Sección embebible del tab Tablero del Portal del cliente.
 *
 * Portal V2 refactor (2026-05-28): el panel sólo ASIGNA / DESASIGNA sets de columnas
 * existentes del Tablero general. Los sets se crean/editan exclusivamente en el modal
 * "Configurar columnas" del Tablero. Cuando un set asignado se edita allá, propaga
 * automáticamente (modulo el prompt de impacto en el Tablero general).
 */
export function ClientPortalDashboardSection({ clientId, onCountChange }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState(MODE_CARDS_V1);
    const [assignments, setAssignments] = useState([]);
    const [savingMode, setSavingMode] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [unassignTarget, setUnassignTarget] = useState(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const columnHeaderInfo = useMemo(() => getColumnHeaderInformation(t), [t]);
    // El toggle Tablero/Cards solo se muestra al equipo Dashbo; las agencias
    // quedan estandarizadas en Tablero (default backend TABLE_V2).
    const showModalityToggle = isPortalModalityToggleEnabled();

    const fetchAssignments = useCallback(() => {
        if (!clientId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        ClientDashboardColumnsSetsApi.listAssignments(clientId)
            .then((response) => {
                if (response && (response.status === 'OK' || response.sets)) {
                    const data = response.data || response;
                    setMode(data.mode || MODE_CARDS_V1);
                    setAssignments(Array.isArray(data.sets) ? data.sets : []);
                } else {
                    toast.error(t('client_portal_dashboard.load_error'));
                }
                setLoading(false);
            })
            .catch(() => {
                toast.error(t('client_portal_dashboard.load_error'));
                setLoading(false);
            });
    }, [clientId, t]);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);

    // Reportar la cantidad de sets asignados hacia arriba (resumen de la card del módulo).
    useEffect(() => {
        if (onCountChange) onCountChange(assignments.length);
    }, [assignments.length, onCountChange]);

    const ordered = useMemo(
        () => [...assignments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [assignments]
    );

    const handleToggleMode = (e) => {
        const newMode = e.target.checked ? MODE_TABLE_V2 : MODE_CARDS_V1;

        if (newMode === MODE_TABLE_V2 && assignments.length === 0) {
            toast.info(t('client_portal_dashboard.cannot_enable_table_without_templates'));
            return;
        }

        setSavingMode(true);
        ClientDashboardColumnsSetsApi.setMode(clientId, newMode)
            .then((response) => {
                if (response && (response.mode || response.status === 'OK')) {
                    setMode(newMode);
                    toast.success(t('client_portal_dashboard.mode_saved'));
                } else {
                    toast.error(t('client_portal_dashboard.mode_save_error'));
                }
                setSavingMode(false);
            })
            .catch(() => {
                toast.error(t('client_portal_dashboard.mode_save_error'));
                setSavingMode(false);
            });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = ordered.findIndex((a) => a.id === active.id);
        const newIndex = ordered.findIndex((a) => a.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const reordered = arrayMove(ordered, oldIndex, newIndex);
        const updated = reordered.map((a, idx) => ({ ...a, order: idx }));
        setAssignments(updated);

        ClientDashboardColumnsSetsApi.reorderAssignments(clientId, reordered.map((a) => a.id))
            .then((response) => {
                if (!response || response.status === 'ERROR') {
                    toast.error(t('client_portal_dashboard.reorder_error'));
                    fetchAssignments();
                }
            })
            .catch(() => {
                toast.error(t('client_portal_dashboard.reorder_error'));
                fetchAssignments();
            });
    };

    const handleUnassignConfirm = () => {
        if (!unassignTarget) return;
        ClientDashboardColumnsSetsApi.unassignSet(clientId, unassignTarget.id)
            .then(() => {
                toast.success(t('client_portal_dashboard.unassigned'));
                setUnassignTarget(null);
                fetchAssignments();
            })
            .catch(() => {
                toast.error(t('client_portal_dashboard.save_error'));
            });
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="box-loader">
                    <Spinner color="positive" />
                </div>
            );
        }

        return (
            <>
                {showModalityToggle && (
                    <>
                        <div className="client-dashboard-tpl-mode-row">
                            <div className="client-dashboard-tpl-mode-info">
                                <span className="client-dashboard-tpl-mode-label">
                                    {t('client_portal_dashboard.mode_label')}
                                </span>
                                <span className="client-dashboard-tpl-mode-current">
                                    {mode === MODE_TABLE_V2
                                        ? t('client_portal_dashboard.mode_table_v2')
                                        : t('client_portal_dashboard.mode_cards_v1')}
                                </span>
                                <span className="client-dashboard-tpl-mode-desc">
                                    {mode === MODE_TABLE_V2
                                        ? t('client_portal_dashboard.mode_table_desc')
                                        : t('client_portal_dashboard.mode_cards_desc')}
                                </span>
                            </div>
                            <div className="client-dashboard-tpl-mode-switch">
                                {savingMode ? (
                                    <Spinner color="positive small" />
                                ) : (
                                    <Switch
                                        name="portal_dashboard_mode"
                                        checked={mode === MODE_TABLE_V2}
                                        onChange={handleToggleMode}
                                        style={{ margin: 0 }}
                                    />
                                )}
                            </div>
                        </div>

                        {mode === MODE_CARDS_V1 && assignments.length > 0 && (
                            <div className="client-dashboard-tpl-mode-off-hint">
                                <FaIcon iconName="circle-info" />
                                {' '}
                                {t(
                                    assignments.length === 1
                                        ? 'client_portal_dashboard.mode_off_hint_with_sets_one'
                                        : 'client_portal_dashboard.mode_off_hint_with_sets_other',
                                    { count: assignments.length }
                                )}
                            </div>
                        )}

                        <div className="client-dashboard-tpl-divider" />
                    </>
                )}

                <div className="client-dashboard-tpl-header-row">
                    <div className="client-dashboard-tpl-header-text">
                        <p className="client-dashboard-tpl-header-hint">
                            {t('client_portal_dashboard.header_hint')}
                        </p>
                    </div>
                    {assignments.length > 0 && (
                        <div className="client-dashboard-tpl-header-actions">
                            <button
                                type="button"
                                className="btn -alt client-dashboard-tpl-create-btn"
                                onClick={() => setShowAssignModal(true)}
                            >
                                <FaIcon iconName="plus" />
                                {' '}{t('client_portal_dashboard.assign_set')}
                            </button>
                        </div>
                    )}
                </div>

                {assignments.length === 0 ? (
                    <div className="client-dashboard-tpl-empty-state">
                        <FaIcon iconName="table-columns" className="client-dashboard-tpl-empty-state-icon" />
                        <p className="client-dashboard-tpl-empty-state-title">
                            {t('client_portal_dashboard.empty_state_title')}
                        </p>
                        <p className="client-dashboard-tpl-empty-state-body">
                            {t('client_portal_dashboard.empty_state_body')}
                        </p>
                        <div className="client-dashboard-tpl-empty-state-ctas">
                            <button
                                type="button"
                                className="btn -alt client-dashboard-tpl-empty-state-cta"
                                onClick={() => setShowAssignModal(true)}
                            >
                                <FaIcon iconName="plus" />
                                {' '}{t('client_portal_dashboard.assign_existing_set')}
                            </button>
                            <Link to="/" className="btn -alt -negative">
                                <FaIcon iconName="arrow-up-right-from-square" />
                                {' '}{t('client_portal_dashboard.go_to_dashboard_create')}
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="client-dashboard-tpl-list">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={ordered.map((a) => a.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {ordered.map((a, idx) => (
                                    <AssignmentRow
                                        key={a.id}
                                        assignment={a}
                                        isDraggable={ordered.length > 1}
                                        isFirst={idx === 0}
                                        t={t}
                                        onUnassign={setUnassignTarget}
                                        columnHeaderInfo={columnHeaderInfo}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="client-portal-dashboard-section">
            {renderContent()}

            <AssignColumnSetToPortalModal
                isOpen={showAssignModal}
                toggle={() => setShowAssignModal(false)}
                clientId={clientId}
                currentAssignments={assignments}
                onSaved={fetchAssignments}
            />

            {unassignTarget && (
                <YesNoModal
                    isOpen={!!unassignTarget}
                    title={t('client_portal_dashboard.unassign_title')}
                    question={t('client_portal_dashboard.unassign_confirm', { name: unassignTarget.name })}
                    onYesAnswer={handleUnassignConfirm}
                    onNoAnswer={() => setUnassignTarget(null)}
                />
            )}
        </div>
    );
}

function prettify(columnId) {
    if (!columnId) return '';
    if (columnId.startsWith('FLEX_INDICATOR||')) {
        const parts = columnId.replace('FLEX_INDICATOR||', '').split('||');
        return parts.join(' · ').replace(/_/g, ' ').toLowerCase();
    }
    return columnId.replace(/_COLUMN$/, '').replace(/_/g, ' ').toLowerCase();
}
